import React, { useContext, useState, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';
import { Button, Link, Spinner } from '@ergeon/core-components';
import { PhoneArrowUpRightIcon, UserIcon } from '@ergeon/icons/hero/micro';

import { TwilioContext } from '../../context-providers/TwilioContext';
import { ROUTES } from '../../routes';
import { CallInfoAggregated } from '../../types';

type HistoricalCallButtonsProps = {
  data: CallInfoAggregated;
};

export const useHistoricalCallButtons = ({
  data,
}: HistoricalCallButtonsProps): React.JSX.Element[] => {
  const navigate = useNavigate();
  const { makeCall, selectedHistoricalCall, setSelectedHistoricalCall } = useContext(TwilioContext);
  const [isCallingAgain, setIsCallingAgain] = useState(false);

  const handleCallAgain = useCallback(async() => {
    if (!data.phoneNumber) return;

    setIsCallingAgain(true);
    try {
      const newCall = await makeCall(data.phoneNumber);
      if (selectedHistoricalCall) {
        setSelectedHistoricalCall(undefined);
      }

      if (!newCall) {
        console.error('Failed to initiate call');
        return;
      }

      // CallSid is populated asynchronously by Twilio SDK after connect() resolves
      // Wait for the 'ringing' event when the CallSid becomes available
      const waitForCallSid = (): Promise<string | undefined> => {
        return new Promise((resolve) => {
          // Check if CallSid is already available
          if (newCall.parameters?.CallSid) {
            resolve(newCall.parameters.CallSid);
            return;
          }

          // Wait for ringing event when CallSid should be populated
          const onRinging = () => {
            newCall.off('ringing', onRinging);
            resolve(newCall.parameters?.CallSid);
          };

          // Also handle disconnect in case call fails before ringing
          const onDisconnect = () => {
            newCall.off('ringing', onRinging);
            newCall.off('disconnect', onDisconnect);
            resolve(newCall.parameters?.CallSid);
          };

          newCall.on('ringing', onRinging);
          newCall.on('disconnect', onDisconnect);

          // Timeout fallback after 10 seconds
          setTimeout(() => {
            newCall.off('ringing', onRinging);
            newCall.off('disconnect', onDisconnect);
            resolve(newCall.parameters?.CallSid);
          }, 10000);
        });
      };

      const newCallSid = await waitForCallSid();
      if (newCallSid) {
        navigate(ROUTES.callNew.$buildPath({ searchParams: { sid: newCallSid } }));
      } else {
        console.error('Call initiated but no CallSid received after waiting');
      }
    } catch (error) {
      console.error('Failed to make call:', error);
    } finally {
      setIsCallingAgain(false);
    }
  }, [data.phoneNumber, makeCall, selectedHistoricalCall, setSelectedHistoricalCall, navigate]);

  const buttons: React.JSX.Element[] = [];

  // Call Again button - only show if phone number exists
  if (data.phoneNumber) {
    buttons.push(
      <Button
        disabled={isCallingAgain}
        flavor="success"
        key="callagain"
        onClick={handleCallAgain}
        size="small"
        taste="solid"
        title="Call Again">
        {isCallingAgain ? (
          <Spinner active className="shrink-0" />
        ) : (
          <PhoneArrowUpRightIcon className="size-4 shrink-0" />
        )}{' '}
        Call Again
      </Button>,
    );
  }

  // Go to Customer - Link-based (like DTMF button pattern)
  if (data.customer) {
    const customerUrl = ROUTES.customer.$buildPath({ params: { customerId: data.customer.id } });
    buttons.push(
      <Link
        className="contents [&>span]:contents"
        href={customerUrl}
        key="gotocustomer"
        unstyled>
        <Button
          aria-label="Go to customer details"
          flavor="regular"
          size="small"
          taste="line"
          title="Go to Customer">
          <UserIcon className="size-4 shrink-0" />
          Go to Customer
        </Button>
      </Link>,
    );
  }

  return buttons;
};
